/**
 * Check autocomplete – combobox over the full checks list with free-text fallback.
 * - Typing filters the dropdown.
 * - Selecting a check stores its id + name (and shows an image when one is mapped).
 * - Pressing Enter or blurring without a selection commits the typed text as free text.
 */
import { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useChecks } from '@/queries/hooks';
import type { CheckDto } from '@/queries/types';
import { getCheckImage } from './data/check-images-data';

export interface CheckAutocompleteValue {
  text: string;
  checkId: number | null;
}

interface CheckAutocompleteProps {
  value: CheckAutocompleteValue;
  onChange: (value: CheckAutocompleteValue) => void;
  placeholder?: string;
  className?: string;
}

export function CheckAutocomplete({
  value,
  onChange,
  placeholder = 'Sometimes',
  className = '',
}: CheckAutocompleteProps) {
  const { data: checks } = useChecks();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCheck = useMemo(() => {
    if (!checks || value.checkId == null) return null;
    return checks.find((c) => c.id === value.checkId) ?? null;
  }, [checks, value.checkId]);

  const selectedImage = selectedCheck
    ? getCheckImage(selectedCheck.hintLabel, selectedCheck.name)
    : undefined;

  const filtered = useMemo(() => {
    if (!checks) return [];
    const sorted = [...checks].sort((a, b) =>
      (a.hintLabel ?? a.name).localeCompare(b.hintLabel ?? b.name)
    );
    if (!search) return sorted.slice(0, 100);
    const s = search.toLowerCase();
    return sorted.filter(
      (c) =>
        c.name.toLowerCase().includes(s) ||
        (c.hintLabel && c.hintLabel.toLowerCase().includes(s)) ||
        (c.tags && c.tags.toLowerCase().includes(s)) ||
        c.locationName.toLowerCase().includes(s)
    );
  }, [checks, search]);

  // Close on outside click — commit free text on close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        commitFreeText();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search]);

  useEffect(() => {
    if (open) {
      setSearch(value.text);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [open, value.text]);

  const commitFreeText = () => {
    if (search !== value.text || value.checkId != null) {
      onChange({ text: search, checkId: null });
    }
  };

  const handleSelect = (check: CheckDto) => {
    onChange({ text: check.hintLabel ?? check.name, checkId: check.id });
    setSearch('');
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange({ text: '', checkId: null });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitFreeText();
      setOpen(false);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setSearch('');
    }
  };

  // Closed: image (if check + mapping) or text label, with chevron/clear affordances
  if (!open) {
    const hasContent = value.text.length > 0;
    return (
      <div
        ref={containerRef}
        className={`flex-1 flex items-center gap-1 px-2 py-0.5 bg-gray-700/50 border border-gray-600/50 rounded cursor-pointer hover:border-gray-500 transition-colors min-w-[120px] ${className}`}
        onClick={() => setOpen(true)}
      >
        {selectedImage ? (
          <img
            src={selectedImage}
            alt={value.text}
            className="h-6 w-6 object-contain flex-shrink-0"
            title={value.text}
          />
        ) : null}
        <span
          className={`flex-1 text-sm truncate ${hasContent ? 'text-white' : 'text-gray-500'}`}
        >
          {hasContent ? value.text : placeholder}
        </span>
        {hasContent ? (
          <button
            onClick={handleClear}
            className="text-gray-500 hover:text-white flex-shrink-0"
            title="Clear"
          >
            <X size={12} />
          </button>
        ) : (
          <ChevronDown size={14} className="text-gray-500 flex-shrink-0" />
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative flex-1 min-w-[120px] ${className}`}>
      <div className="flex items-center gap-1 px-2 py-0.5 bg-gray-700/50 border border-gray-600/50 rounded">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-white outline-none placeholder-gray-500 min-w-0"
        />
        <ChevronDown size={14} className="text-gray-500 rotate-180 flex-shrink-0" />
      </div>

      <div className="absolute z-50 top-full left-0 mt-1 w-[280px] max-h-[250px] overflow-y-auto bg-gray-800 border border-gray-600 rounded-lg shadow-xl">
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-xs text-gray-500">
            No checks match — press Enter to keep "{search}" as free text.
          </div>
        ) : (
          filtered.map((check) => {
            const img = getCheckImage(check.hintLabel, check.name);
            const label = check.hintLabel ?? check.name;
            return (
              <button
                key={check.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(check)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-700 transition-colors flex items-center gap-2"
              >
                {img ? (
                  <img src={img} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
                ) : (
                  <span className="w-5 h-5 flex-shrink-0" />
                )}
                <span className="text-white truncate">{label}</span>
                <span className="text-gray-500 text-xs ml-auto flex-shrink-0 truncate max-w-[100px]">
                  {check.locationName}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
